import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClipboardCheck, ShieldAlert, CheckCircle2, ListFilter, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import MonthlyRegister from "@/components/MonthlyRegister";

const FACE_LABEL = {
  matched: "Face verified",
  not_matched: "Face mismatch",
  disabled: "Photo only",
  no_reference: "Photo only",
  error: "Photo only",
};

// Late/early minutes read as plain "—" until a shift is assigned to the
// employee (Attendance Locations → Shift Assignment) — the rule engine has
// nothing to compare against otherwise, so this is expected, not a bug.
function Deviation({ minutes, outcome }) {
  if (minutes === undefined || minutes === null) return <span className="text-slate-400">—</span>;
  if (!minutes) return <span className="text-slate-400">On time</span>;
  const cls = outcome === "half_day" ? "text-red-600" : outcome === "grace_used" ? "text-amber-700" : "text-slate-600";
  return <span className={cls}>{minutes} min</span>;
}

function toYMD(d) {
  return d.toISOString().slice(0, 10);
}

const attendanceTracked = (e) => !e.top_management && !e.exempt_from_rules && !["Contract", "Consultant"].includes(e.employment?.employee_type);

export default function AttendanceMonitor() {
  const qc = useQueryClient();

  // ---- Attendance Records: filter by employee (or All Employees) + date range, with download ----
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [recEmployee, setRecEmployee] = useState("all");
  const [recFrom, setRecFrom] = useState(toYMD(firstOfMonth));
  const [recTo, setRecTo] = useState(toYMD(today));
  const [downloading, setDownloading] = useState(null); // "xlsx" | "csv" | null

  const { data: employees } = useQuery({
    queryKey: ["employeesForAttendance"], queryFn: async () => (await api.get("/employees")).data,
  });

  const { data: records, isLoading: recordsLoading, refetch: refetchRecords, isFetching: recordsFetching } = useQuery({
    queryKey: ["attRecords", recEmployee, recFrom, recTo],
    queryFn: async () => (await api.get("/attendance/admin/report", {
      params: { employee_id: recEmployee, date_from: recFrom, date_to: recTo },
    })).data,
  });

  const downloadRecords = async (fmt) => {
    setDownloading(fmt);
    try {
      const res = await api.get("/attendance/admin/report/export", {
        params: { employee_id: recEmployee, date_from: recFrom, date_to: recTo, fmt },
        responseType: "blob",
      });
      // Server names the file (employee/period-aware); pull it from the
      // response header instead of guessing so downstream matches exactly
      // what was exported.
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `Attendance.${fmt}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      // responseType "blob" means an error body also arrives as a Blob, not
      // parsed JSON — read it back out as text before trying to show it.
      let detail = "Unable to download attendance records";
      const errBlob = e.response?.data;
      if (errBlob instanceof Blob) {
        try { detail = apiError(JSON.parse(await errBlob.text())?.detail) || detail; } catch { /* keep default */ }
      }
      toast.error(detail);
    } finally {
      setDownloading(null);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["attToday"], queryFn: async () => (await api.get("/attendance/admin/today")).data,
    refetchInterval: 60000,
  });
  const acknowledge = useMutation({
    mutationFn: async (id) => (await api.put(`/attendance/admin/events/${id}/acknowledge`)).data,
    onSuccess: () => { toast.success("Acknowledged"); qc.invalidateQueries({ queryKey: ["attToday"] }); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader overline="Attendance" title="Today" icon={ClipboardCheck}
        subtitle="Live view of who has checked in today, where, how it was verified, and any late/early deviation." />

      {isLoading ? <Loading /> : (data || []).length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="No check-ins yet today" hint="This updates automatically as staff check in." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden mb-8">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Employee</TableHead>
              <TableHead className="overline text-slate-500">Location</TableHead>
              <TableHead className="overline text-slate-500">Check-in</TableHead>
              <TableHead className="overline text-slate-500">Check-out</TableHead>
              <TableHead className="overline text-slate-500">Late</TableHead>
              <TableHead className="overline text-slate-500">Early</TableHead>
              <TableHead className="overline text-slate-500">Verification</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="overline text-slate-500">Flag</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((ev) => (
                <TableRow key={ev.id} className="table-row-i">
                  <TableCell className="font-medium text-navy">{ev.employee_name} <span className="text-slate-400 font-mono-i text-xs">({ev.employee_code})</span></TableCell>
                  <TableCell className="text-slate-600">{ev.location_name}</TableCell>
                  <TableCell className="text-slate-600">{ev.check_in_time ? new Date(ev.check_in_time).toLocaleTimeString() : "—"}</TableCell>
                  <TableCell className="text-slate-600">{ev.check_out_time ? new Date(ev.check_out_time).toLocaleTimeString() : "—"}</TableCell>
                  <TableCell><Deviation minutes={ev.late_minutes} outcome={ev.late_outcome} /></TableCell>
                  <TableCell><Deviation minutes={ev.early_minutes} outcome={ev.early_outcome} /></TableCell>
                  <TableCell className="text-slate-600">{FACE_LABEL[ev.face_status] || "Photo only"}</TableCell>
                  <TableCell><StatusBadge status={ev.status} /></TableCell>
                  <TableCell>
                    {!ev.emergency_cover ? <span className="text-slate-300">—</span> : ev.emergency_acknowledged ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Reconciled</span>
                    ) : (
                      <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2"
                        onClick={() => acknowledge.mutate(ev.id)} disabled={acknowledge.isPending}>
                        <ShieldAlert className="h-3.5 w-3.5 mr-1" /> Emergency cover — Acknowledge
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <MonthlyRegister />

      <PageHeader overline="Attendance" title="Attendance Records" icon={ListFilter}
        subtitle="Pick an employee (or All Employees) and a date range, then view or download the records — as an Excel or CSV file." />

      <Card className="border-slate-200 shadow-none p-4 mb-6">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="overline text-slate-500">Employee</Label>
            <Select value={recEmployee} onValueChange={setRecEmployee}>
              <SelectTrigger className="mt-1 bg-white" data-testid="att-record-employee"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">All Employees</SelectItem>
                {(employees || []).filter(attendanceTracked).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({e.employee_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-slate-500">Date From</Label>
            <Input type="date" value={recFrom} onChange={(e) => setRecFrom(e.target.value)} className="mt-1 bg-white" data-testid="att-record-from" />
          </div>
          <div>
            <Label className="overline text-slate-500">Date To</Label>
            <Input type="date" value={recTo} onChange={(e) => setRecTo(e.target.value)} className="mt-1 bg-white" data-testid="att-record-to" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => refetchRecords()} disabled={recordsFetching} data-testid="att-record-search">
              <ListFilter className="h-4 w-4 mr-1" /> Apply
            </Button>
            <Button variant="outline" className="text-teal border-teal/30 hover:bg-teal/5" onClick={() => downloadRecords("xlsx")}
              disabled={downloading !== null || !records?.length} data-testid="att-record-download-xlsx">
              <FileSpreadsheet className="h-4 w-4 mr-1" /> {downloading === "xlsx" ? "Preparing…" : "Excel"}
            </Button>
            <Button variant="outline" onClick={() => downloadRecords("csv")}
              disabled={downloading !== null || !records?.length} data-testid="att-record-download-csv">
              <Download className="h-4 w-4 mr-1" /> {downloading === "csv" ? "Preparing…" : "CSV"}
            </Button>
          </div>
        </div>
      </Card>

      {recordsLoading ? <Loading /> : (records || []).length === 0 ? (
        <EmptyState icon={ListFilter} title="No attendance records for this selection"
          hint="Try a wider date range, or choose a different employee." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <div className="max-h-[520px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50 z-10"><TableRow className="bg-slate-50">
                <TableHead className="overline text-slate-500">Employee</TableHead>
                <TableHead className="overline text-slate-500">Date</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="overline text-slate-500">Check-in</TableHead>
                <TableHead className="overline text-slate-500">Check-out</TableHead>
                <TableHead className="overline text-slate-500">Hours</TableHead>
                <TableHead className="overline text-slate-500">Location</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={i} className="table-row-i">
                    <TableCell className="font-medium text-navy">{r.employee_name} <span className="text-slate-400 font-mono-i text-xs">({r.employee_code})</span></TableCell>
                    <TableCell className="text-slate-600">{r.date}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-slate-600">{r.check_in_time || "—"}</TableCell>
                    <TableCell className="text-slate-600">
                      {r.check_out_time || (r.missed_checkout
                        ? <span className="text-red-600 font-medium" title="Checked in but never checked out — becomes Half Day unless corrected">Missing</span>
                        : "—")}
                      {r.missed_checkout_penalty && <span className="block text-[11px] text-red-500">Half Day (no check-out)</span>}
                    </TableCell>
                    <TableCell className="text-slate-600">{r.hours_worked ?? "—"}</TableCell>
                    <TableCell className="text-slate-600">{r.location || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">{records.length} record{records.length === 1 ? "" : "s"}</div>
        </Card>
      )}
    </div>
  );
}
